import { IEvent } from "@Interfaces/redux/store/IEvent";
import { logError } from "repoV2/utils/common/error/error";
import { IHost } from "@Interfaces/redux/store/IHost";
import { IUser } from "@Interfaces/redux/store/IUser";
import { IUtils } from "@Interfaces/redux/store/IUtils";
import { useAccessRestriction } from "@Modules/eventPage/utils/useAccessRestriction";
import RebookingConfirmationModal from "@Modules/modals/components/UserDetails/components/RebookingConfirmationModal/RebookingConfirmationModal";
import useRebookingConfirmationModalContainer from "@Modules/modals/components/UserDetails/components/RebookingConfirmationModal/useRebookingConfirmationModalContainer";
import { EXTRA_FIELD as USER_DETAILS_FORM_EXTRA_FIELD } from "@Modules/modals/components/UserDetails/FormData";
import { IFetchDataAction } from "@Redux/sagas/api";
import {
    getIsSelectingScheduleMandatory,
    getIfSlotsAvailable,
} from "@Utils/event";
import { getUserDetailsFormKey } from "@Utils/form";
import { isEmpty } from "@Utils/common";
import { formatUserPaymentAuthPayload } from "@Utils/user";
import {
    evaluatePaymentMethod,
    clearPaymentCache,
    callPaymentGateway,
    calculatePaymentInfoForSingleOffering,
    IPaymentInfoForSingleOffering,
} from "@Utils/payment";
import {
    EVENT_TYPE,
    EVENT_STATUS,
    EVENT_ENUM,
    AVAILABILITY_CHOICE,
} from "@Utils/constants/event";
import { createAction, ACTION_TYPES, IAction } from "@Utils/constants/actions";
import { SELECTORS } from "@Utils/constants/selectors";
import { MODAL_TYPES, IModalTypes } from "@Utils/constants/modal";
import { FETCH_STATUS, API_ACTION_TYPES } from "@Utils/constants/api";
import {
    TEMPLATE_DATA,
    STORAGE_KEYS,
    REDUX_DATA_KEYS,
    QUERY_PARAMS,
} from "@Utils/constants/keys";
import { useProcessing } from "@Utils/hooks/rendering";
import { useModal, IUseModal } from "@Utils/hooks/modal";
import { useFetchStatus } from "@Utils/hooks/api";
import { useData } from "@Utils/hooks/data";
import { setCssVar, getCssKeyName } from "@Utils/style";
import useCustomMediaQuery from "@Utils/hooks/useCustomMediaQuery";
import { deleteUrlParams } from "@Utils/urls";
import { isBefore } from "date-fns";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isDefined } from "repoV2/utils/common/dataTypes/common";
import { isInstallmentsPlanType } from "repoV2/utils/common/listing";
import {
    CURRENCY_STRINGS,
    GATEWAYS_ENUM,
    GATEWAYS_SUPPORTED,
    TRANSACTION_STATUSES,
} from "repoV2/constants/payment";
import { USER_DETAILS_FORM_FIELDS } from "repoV2/constants/user&auth&login/userDetails";
import {
    getLocalStorageItem,
    getSessionStorageItem,
    setLocalStorageItem,
    setSessionStorageItem,
} from "repoV2/utils/common/storage/getterAndSetters";
import { parseDateTime } from "@Utils/parseDateTime";
import { isBrowser } from "repoV2/utils/common/render/window";
import { getCountryIsIndia } from "repoV2/utils/common/location/location";
import { IListing } from "repoV2/features/Listing/Listing.interfaces";
import { LISTING_TYPES } from "repoV2/constants/listing";
import { URL_SEARCH_PARAMS } from "repoV2/constants/urls&routing/urlParams";
import { postAnalytics } from "repoV2/features/Common/Analytics/Analytics.utils";
import { ANALYTICS_EVENTS } from "repoV2/features/Common/Analytics/Analytics.constants";
import { checkIsStringEmpty } from "repoV2/utils/common/dataTypes/string";
import { URL_SEARCH_PARAMS as THANKYOU_URL_SEARCH_PARAMS } from "@Modules/thankyou/constants/thankyou.contants";
import {
    IAddToCartCtaProps,
    ICTAButtons,
    IMainCtaProps,
} from "../components/CTAButtons/CTAButton";
import CompactEventPage from "../components/CompactEventPage";
import { ICompactEventPageModule } from "../components/CompactEventPage/ICompactEventPageModule";
import { getShowCompactEventPage } from "../components/CompactEventPage/utils/getShowCompactEventPage";
import CompleteEventPage from "../components/CompleteEventPage";
import { ICompleteEventPageModule } from "../components/CompleteEventPage/ICompleteEventPageModule";
import { IExpressInterestProps } from "../components/ExpressInterest/ExpressInterest";
import {
    IEarlyBird,
    IPaymentSummary,
    IScheduleSelectorProps,
    ITicketCount,
    IUpsellListings,
    IWrapperComponentType,
} from "../components/Interfaces";
import { IPlanDropdown } from "../components/PlanDropdown/IPlanDropdown";
import { usePlanDropdownContext } from "../components/PlanDropdown/utils/PlanDropdownContext";
import { useReadIsOfferingSelectedInPlan } from "../components/PlanDropdown/utils/usePlanLeafSelected";
import { IRecentSalesPopup } from "../components/RecentSalesPopup/RecentSalesPopup";
import { useInstalmentsDetailsContext } from "../components/PaymentPlan/utils/InstalmentsDetailsContext";
import { useRenewBookingFormContext } from "../components/RenewBookingForm/utils/RenewBookingFormContext";
import { IReviewsProps } from "../components/Reviews/IReviews";
import { getShowUpsellListings } from "../components/UpsellListings/utils/getShowUpsellListings";
import {
    evaluateHideAddToCartCta,
    getCartData,
    getIsInCartFlow,
} from "../utils/cart";
import { getEarlyBirdObj } from "../utils/earlyBird";
import { evaluateHideDiscount } from "../utils/evaluateHideDiscount";
import { getAreIntlPaymentsEnabled } from "../utils/getAreIntlPaymentsEnabled";
import { getBillingStateNonMandatory } from "../utils/getBillingStateNonMandatory";
import { getEventCurrency } from "../utils/getEventCurrency";
import { getPricePerHead } from "../utils/getInitialPrice";
import { getListingMainCtaText } from "../utils/getListingMainCtaText";
import { getShowGstOption } from "../utils/getShowGstOption";
import { handleDisabledCtaClick } from "../utils/handleDisabledCtaClick";
import { handleEventStarted } from "../utils/handleEventStarted";
import { useSetQuantity } from "../utils/merchandise";
import { useOpenEventPageModal } from "../utils/modalFunctionalities";
import { evaluateRenderMobileView } from "../utils/render";
import { useNeedUserInputToProceed } from "../utils/useNeedUserInputToProceed";
import { useSetCurrentPlan } from "../utils/useSetCurrentPlan";
import { useSetNumberOfCustomers } from "../utils/useSetNumberOfCustomers";
import { IEventModule } from "./IEventModule";
import { getEventModuleChildProps } from "./data";
import { getCustomerDetailsForPaymentPayload } from "../utils/getCustomerDetailsForPaymentPayload";
import { getCanSkipUserDetailsForm } from "../utils/getCanSkipUserDetailsForm";
import { useSetUpsellListingProps } from "../utils/useSetUpsellListingsProps";
import { useSetPaymentSummaryProps } from "../utils/useSetPaymentSummaryProps";
import { useSetRemoveOverridesFromUDMODB } from "../utils/overrideUDMODB";

// Original dev's note:
// A brief on how this module has been built
// https://img.devrant.com/devrant/rant/r_3898730_rZ4yd.jpg

export const OldEventPageTemplate = ({
    eventId,
    status: paymentStatus,
    hostName,
    transactionId,
    initBooking,
    initContact,
    directBookingDiscount,
    // If hideSelf is passed as prop, then the event page is open as a modal
    hideSelf,
    urlQueryParams,
    userFlow, // to restrict or enable some functionalities in specific flows
    addToCartCallback,
    isEventModal = false,
}: IEventModule.IProps): JSX.Element => {
    const dynamicLink = urlQueryParams?.[QUERY_PARAMS.DYNAMIC_LINK] as string;
    const planId = urlQueryParams?.[QUERY_PARAMS.PLAN_ID] as string;
    const slug = urlQueryParams?.[URL_SEARCH_PARAMS.SLUG] as string;
    const wrapperComponentType: IWrapperComponentType = hideSelf
        ? "modal"
        : "eventPage";

    const communityUuid = urlQueryParams?.[
        URL_SEARCH_PARAMS.COMMUNITY_ID
    ] as string;

    const enablePaypalQueryParam =
        urlQueryParams?.[QUERY_PARAMS.ENABLE_PAYPAL] === "true";

    const event: IEvent.IStore = useSelector(SELECTORS.event);
    const host: IHost.IStore = useSelector(SELECTORS.host);
    const {
        openModal,
        closeModal: closeModalBase,
        modalInfo,
        setModalInfo,
    } = useModal();

    const closeModalRef = useRef(closeModalBase);
    const { setCurrentPlanId } = useSetCurrentPlan();
    useEffect(() => {
        closeModalRef.current = closeModalBase;
        // TODO @aakarsh: please explain
    }, [closeModalBase]);

    const closeModal = closeModalRef.current;

    const dispatch = useDispatch();
    const router = useRouter();

    const { getDropdownSelectorsProps } = usePlanDropdownContext();

    // The event page is rendering twice when opened as a page (not a modal) and so the analytics
    // call is going twice. To mitigate that till the double rendering can be solved, a track of
    // whether the call has already been made is being stored.
    // * NOTE: This value can be used in general to check if it is a re-render or not
    const { data: hasLogged, setData: setLogged } =
        useData<boolean>("completelyLoad");
    const {
        data: startBookingFlowAnalyticsTriggered,
        setData: setStartBookingFlowAnalyticsTriggered,
    } = useData<boolean>(
        REDUX_DATA_KEYS.START_BOOKINGS_FLOW_ANALYTICS_TRIGGERED
    );

    // @dev setting the currentPlanId here on mount in case of plan event
    // unsetting it on mount in case of offering event (no plan)
    // as this is the common component being used in EventModal and EventPage
    useEffect(() => {
        if (planId) setCurrentPlanId(planId as string);
        else setCurrentPlanId(null);
        // @dev
        // cannot unset currentPlanId on unmount in case of EventModal
        // as init_booking/UserDetails Modal unmounts this component
        // hence will clear the currentPlanId used in payment payload
        // of initiate booking and also cannot check for modalType UserDetails to be opened
        // as this component unmounts before UserDetails Modal mounts.
    }, [planId]);

    const { isProcessing, startProcessing, stopProcessing } = useProcessing();
    const [processingMessage, setProcessingMessage] = useState<string>();

    const {
        modalState: rebookingModalState,
        handleRebookingError,
        closeModal: closeRebookingModal,
    } = useRebookingConfirmationModalContainer();

    // To show or hide the opaque backdrop that hides the event page and only shows an open modal
    const [showOpaqueBackdrop, setShowOpaqueBackdrop] =
        useState<boolean>(false);

    // Using ref for .main to scroll up when the discount box is selected
    const mainScrolledComponentRef = useRef<HTMLDivElement>(null);
    const scheduleSelectorRef = useRef<HTMLDivElement>(null);

    const hostData: IHost.IHostData = host.dataHost.profile_data!;
    const {
        is_customer_details_updation_allowed: isCustomerDetailsUpdationAllowed,
    } = hostData ?? {};

    const {
        paymentInfo: {
            slots,
            quantity,
            numberOfCustomers,
            otherListings: selectedUpsellOfferingsQuantities, // quantities of listings selected from upsell
        },
        list: eventDataList,

        discount: {
            discountData,
            appliedDiscountCodeType,
            customerEmail: loyaltyDiscountEmail,
        },
    }: IEvent.IStore = event;

    const eventData: IEvent.IEventData = eventDataList[eventId];
    const {
        sessions_visible_on_booking_page: sessionsLimit,
        limit_sessions: isLimitSessionsEnabled,
    } = eventData?.metadata;

    const { offeringAccessDenied } = useAccessRestriction({
        isPageFilterEnabled: eventData?.is_page_filter_enabled,
        listingUuid: eventId,
    });

    useEffect(() => {
        if (offeringAccessDenied && wrapperComponentType === "eventPage") {
            const redirectUrl = window.location.origin;
            window.location.href = redirectUrl;
        }
    }, [offeringAccessDenied]);

    const {
        variantsData,
        selectedVariant,
        shipping_details: shippingDetails,
        enable_shiprocket: shiprocketEnabled,
        metadata: { batched_flexible_sessions_count },
    } = eventData;

    const repaymentDataFetchStatus = useFetchStatus(
        API_ACTION_TYPES.FETCH_REPAYMENT_DATA
    );

    // Get the redux value of the order details
    const ordersMap = useSelector(SELECTORS.orders);

    useEffect(() => {
        const borderRadius =
            TEMPLATE_DATA[host.template as keyof typeof TEMPLATE_DATA]
                ?.BOOKING_BUTTON_BORDER_RADIUS;
        // To set the border radius of the booking button as per the template
        if (isDefined(borderRadius)) {
            setCssVar(
                getCssKeyName("booking-button-border-radius", "event-page"),
                borderRadius
            );
        }

        // TODO: better implementation will be to pass host.template key and use inline styling
    }, []);

    useEffect(() => {
        const errorCallback = (params: IFetchDataAction.IReturnPayload) => {
            alert(
                params?.response?.message ||
                    "An error occurred in resuming payment. Please try the link again or start again."
            );
            window.location.href = deleteUrlParams(window.location.href, [
                QUERY_PARAMS.ORDER,
                QUERY_PARAMS.ACTION,
            ]);
        };

        if (
            router.query?.[QUERY_PARAMS.ORDER] &&
            router.query?.[QUERY_PARAMS.ACTION] &&
            repaymentDataFetchStatus === FETCH_STATUS.NOT_FETCHED
        ) {
            setShowOpaqueBackdrop(true);
            dispatch(
                createAction(ACTION_TYPES.UTILS.API_CALL, {
                    apiActionType: API_ACTION_TYPES.FETCH_REPAYMENT_DATA,
                    urlArgs: {
                        paymentAction: urlQueryParams?.[QUERY_PARAMS.ACTION],
                        orderId: urlQueryParams?.[QUERY_PARAMS.ORDER],
                    },
                    successCallback: ({ response: { data } }) => {
                        const paymentPayload: Pick<
                            IFetchDataAction.IReturnPayload,
                            "response"
                        > = {
                            response: {
                                status: 200,
                                message: "success",
                                // Here, data consist of the api response of the rebook order api,
                                // which is used to call the payment gateway below
                                data: data || {},
                            },
                        };
                        callPaymentGateway({
                            eventData,
                            optionalCallbacks: {
                                onRazorpayClose: () => {
                                    setShowOpaqueBackdrop(false);
                                    router.replace(
                                        router.pathname,
                                        deleteUrlParams(router.asPath, [
                                            QUERY_PARAMS.ORDER,
                                            QUERY_PARAMS.ACTION,
                                        ]),
                                        { shallow: true }
                                    );
                                },
                            },
                        })(paymentPayload);
                    },
                    errorCallback,
                })
            );
        }
    }, []);

    const updateBookingData = ({
        slotsProp,
        quantityProp,
        customerAddressProp,
        gatewaysSupportedProp,
        isDirectBookingProp,
        numberOfCustomersProp,
        otherListingsProp,
    }: IEventModule.IUpdateBookingData) => {
        // TODO: Fill via  iteration from a map
        dispatch(
            createAction(ACTION_TYPES.EVENT.UPDATE_BOOKING_DATA, {
                ...(isDefined(slotsProp) && { slots: slotsProp }),
                ...(isDefined(quantityProp) && {
                    quantity: quantityProp,
                }),
                ...(isDefined(customerAddressProp) && {
                    customerAddress: customerAddressProp,
                }),
                ...(isDefined(gatewaysSupportedProp) && {
                    gatewaysSupported: gatewaysSupportedProp,
                }),
                ...(isDefined(isDirectBookingProp) && {
                    isDirectBooking: isDirectBookingProp,
                }),
                ...(isDefined(numberOfCustomersProp) && {
                    numberOfCustomers: numberOfCustomersProp,
                }),
                ...(isDefined(otherListingsProp) && {
                    otherListings: otherListingsProp,
                }),
            } as Partial<IEvent.IPaymentInfo>)
        );
    };

    const setQuantity = useSetQuantity();
    const setNumberOfCustomers = useSetNumberOfCustomers();

    const [showLoyaltyDiscountsPopup, setShowLoyaltyDiscountsPopup] =
        useState(false);

    const setAppliedDiscountCodeDetails = (
        appliedDiscountCodeTypeParam: IEvent.IAppliedDiscountCodeType,
        customerEmailParam?: string
    ) => {
        setSessionStorageItem(
            STORAGE_KEYS.DISCOUNT_CODE_TYPE,
            appliedDiscountCodeTypeParam
        );

        dispatch(
            createAction(
                ACTION_TYPES.EVENT.UPDATE_APPLIED_DISCOUNT_CODE_DETAILS,
                {
                    appliedDiscountCodeType: appliedDiscountCodeTypeParam,
                    customerEmail: customerEmailParam,
                }
            )
        );
    };

    const [showLoyaltyDiscountSection, setShowLoyaltyDiscountSection] =
        useState(true);

    useEffect(() => {
        // in case of payment failure, these values are set in session storage
        // need to restore them to component state
        const discountCodeType = getSessionStorageItem(
            STORAGE_KEYS.DISCOUNT_CODE_TYPE
        );

        const customerEmail = getSessionStorageItem(
            STORAGE_KEYS.LOYALTY_DISCOUNT_EMAIL
        );

        if (discountCodeType) {
            setAppliedDiscountCodeDetails(discountCodeType, customerEmail);
        }
    }, []);

    useEffect(() => {
        if (appliedDiscountCodeType === "loyalty") {
            // in case loyalty discount is applied
            // ticketing is disabled and user count is set to 1
            setNumberOfCustomers(1);
        }
    }, [appliedDiscountCodeType]);

    useEffect(() => {
        if (numberOfCustomers > 1) {
            // in case user count is > 1
            // loyalty discounts are disabled
            setShowLoyaltyDiscountSection(false);
        } else {
            // in case user count is <= 1
            // loyalty discounts are enabled
            setShowLoyaltyDiscountSection(true);
        }
    }, [numberOfCustomers]);

    // TODO @aakarsh: is this comment still relevant?
    // * Leave this commented here for future use
    // Currency being used at the user's location(Currently only INR/USD)
    // const userCurrency: string = host?.country?.currency || INR;

    const { eventCurrency, eventCurrencySymbol } = getEventCurrency({
        eventData,
    });

    const {
        type: eventType,
        payment_plan_type,
    }: // updated_price, // Don't use updated_price. Use unitPrice declared below.
    IEvent.IEventData = eventData;

    const areIntlPaymentsEnabled: boolean = getAreIntlPaymentsEnabled({
        host,
        eventData,
    });

    const {
        instalments,
        selectedPaymentType,
        handleApplyDiscountOnInstallments,
        refetchInstallments,
        isDiscountAppliedOnInstallments,
    } = useInstalmentsDetailsContext();
    const [initialPayment] = instalments || [];

    const computedPaymentValues: IPaymentInfoForSingleOffering.IReturn =
        calculatePaymentInfoForSingleOffering({
            eventData,
            quantity,
            slots,
            numberOfCustomers,
            discountData,
            selectedPaymentType,
            initialInstalment: initialPayment,
        });

    const {
        // Boolean flags for certain conditions
        flags: {
            isMerchandise, // Is this listing a merchandise type
            isSingleSlot, // Is it a single slot type
        },
        // Absolute numbers for price values
        priceValues: {
            price, // Grand total price to be paid minus taxes
            totalPrice, // Final price, to be paid
        },
        // Scaling factors
        factors: {
            billingMultiplier, // Number of instances of items being bought for this SKU
        },
    } = computedPaymentValues;

    const isSelectingScheduleMandatory =
        getIsSelectingScheduleMandatory(eventData); // Is this listing a schedule type

    /**
     * The original price of the listing. It will contain the initial price for ALL currencies.
     *
     * Note: `price_per_head` will contain the initial price for ALL currencies.
     * No need to use the value of `price_per_head_usd`.
     */
    const pricePerHead: number = getPricePerHead({
        eventData,
        initialInstalment: initialPayment,
        selectedPaymentType,
    });

    // Whether to hide the price
    const hidePrice: boolean =
        (eventData?.metadata?.hide_price && !dynamicLink) || false;

    const areSlotsAvailable: boolean = getIfSlotsAvailable({ eventData });

    /**
     * If this flag is true, the payment flow will assume that stripe is being used for an international creator
     */
    const hasToShowFullAddress = !!(
        (
            (hostData?.is_international_creator ||
                eventData?.is_full_address) &&
            totalPrice > 0
        ) // No point collecting full address if the payment is free and won't invoke the Stripe gateway
    );

    const showGstOption = getShowGstOption({ event, eventData, host });

    const billingStateNonMandatory = getBillingStateNonMandatory({
        event,
        eventData,
        hostData,
    });

    const { isRenewBooking, handleRenewBookingCtaClick } =
        useRenewBookingFormContext();

    // To keep discount hidden if earlyBird is present, till the mutual exclusivity is to
    // be removed in the upcoming feature, so hide_discount is given a lower precedence
    const hasVariants = !!eventData.variantsData?.variants_display_data;
    const hideDiscount = evaluateHideDiscount({
        eventData,
        hasVariants,
        selectedVariant,
    });

    const isDynamicLinkExpired: boolean =
        !!dynamicLink && eventData.expiry_date
            ? isBefore(parseDateTime(eventData.expiry_date), parseDateTime())
            : false;

    const isSoldOut: boolean =
        eventData.status === EVENT_STATUS.SOLD_OUT || isDynamicLinkExpired;

    const disablePayment: boolean =
        hidePrice ||
        isSoldOut ||
        !areSlotsAvailable ||
        !eventData?.is_active ||
        isDynamicLinkExpired ||
        offeringAccessDenied;

    const showUpsellListings = getShowUpsellListings({
        hostData,
        eventData,
        disablePayment,
        urlQueryParams,
    });

    const earlyBirdObj: IEvent.IEarlyBird | null = getEarlyBirdObj({
        eventData,
        disablePayment,
        isRenewBooking,
        urlQueryParams,
        areIntlPaymentsEnabled,
    });

    const promoCoupons: IEvent.IPromotionalData[] =
        eventData?.promotional_coupons;

    const exitIntentModalToShow: IModalTypes =
        promoCoupons && promoCoupons?.length > 0
            ? MODAL_TYPES.ExitOffer
            : MODAL_TYPES.ExitIntent;

    const {
        needUserInputToProceedData: {
            needUserInputToProceed,
            messageForUserToProceed,
        },
    } = useNeedUserInputToProceed();

    // * Condition for disabling CTAButton component
    // For the logic for the disabled prop for the button:
    // - If a message is being sent, disable the button as the API call is under progress
    // - Or else,
    //      - If the price is to be hidden, check if all the express interest form fields are valid before enabling the button
    // - Or else,
    //      - If it is a no schedule event (with zero slots), it is enabled by default
    //        Or else
    //      - If it is a no workshop(with one slot), it is enabled by default
    //      - If number of slots(slots.length) is greater than zero, enable the button
    //        Or else
    const isScheduleSelected = !isEmpty(slots.filter(slot => slot));

    const isMainCtaDisabled: boolean = (() => {
        if (isProcessing) return true;
        if (needUserInputToProceed) return true;
        if (isMerchandise) {
            return !!(
                !quantity ||
                // disable cta if variants available and not selected
                (!!variantsData?.variants && !selectedVariant) ||
                // pincode required and must be servicable
                (shiprocketEnabled && !shippingDetails?.pincode)
            );
        }
        if (isSelectingScheduleMandatory) return !isScheduleSelected;
        if (!isSingleSlot && !isMerchandise && !EVENT_TYPE.TELEGRAM)
            return slots.length < 1;
        return false;
    })();

    /* @Dev: *NOTE 
    This is a condition to show the booking flow on init_booking url query 
    & if event is webinar and frequency of
    webinar is a single day or others(and limit sessions is 1)
    or there is only one slot for selection */
    const enableWebinarDirectBooking =
        eventData.type === EVENT_TYPE.WEBINAR &&
        ((isLimitSessionsEnabled && sessionsLimit === 1) ||
            eventData?.slots?.length === 1);

    /**
     * read `force_init_booking` to understand more about this
     */
    const enableClassesDirectBooking =
        [
            EVENT_TYPE.ROLLING_CLASSES,
            EVENT_TYPE.GROUP_CLASSES,
            EVENT_TYPE.ONE_ON_ONE_CLASSES,
        ].includes(eventType) && eventData?.metadata?.force_init_booking;

    /** The following section is a hack. To be replaced when Universal Payment is implemented */
    /** START of hack */
    const isDirectBooking = !!(
        isBrowser() &&
        initBooking &&
        //
        // Since we are only checking for main cta disabled.
        //
        // And "Express Interest" and "Buy Now" CTAs... both are considered as main CTAs
        // in diff scenarios.
        //
        // Therefore `isMainCtaDisabled` is not a sufficient check for checking payment is
        // allowed or not. We should use `disablePayment` for that.
        !isMainCtaDisabled &&
        ([
            LISTING_TYPES.WORKSHOPS,
            LISTING_TYPES.RECORDED_CONTENT,
            LISTING_TYPES.NO_SCHEDULE,
            LISTING_TYPES.TELEGRAM,
            LISTING_TYPES.WHATSAPP,
            LISTING_TYPES.BRANDED_COMMUNITY,
            LISTING_TYPES.MERCHANDISE,
        ].includes(eventType) ||
            enableWebinarDirectBooking ||
            enableClassesDirectBooking)
    );

    /**
    function to indicate that we should hide the SSR'd UserDetails modal.
    should be called in every scenario  where we dont open the UserDetails 
    modal for booking, after recieveing the `init_booking` prop

    see definition of `useSetRemoveOverridesFromUDMODB` to understand better
    */
    const removeOverridesFromUDMODB = useSetRemoveOverridesFromUDMODB();

    useEffect(() => {
        if (disablePayment) {
            // remove overrides from UDMODB when payment is disabled
            removeOverridesFromUDMODB();
        }
    }, [disablePayment]);

    useEffect(() => {
        if (!hasLogged) {
            setLogged(true);
        }

        const thankYouPageUuid =
            urlQueryParams?.[THANKYOU_URL_SEARCH_PARAMS.THANKYOU_PAGE_ID];

        // redirected from Sales or Thanks You page
        if ((slug || thankYouPageUuid) && !disablePayment) {
            postAnalytics(ANALYTICS_EVENTS.INITIATE_CHECKOUT, {
                sub_domain: hostName,
                event_source_url: window.location.href,
                num_items: quantity,
                currency: eventData.currency_name,
                value: eventData.price_per_head,
                items: [
                    {
                        item_id: eventData.uuid,
                        item_name: eventData.title,
                    },
                ],
            });
        }
    }, []);

    const postAnalyticalEvent = (eventDetails: Object): void => {
        dispatch(
            createAction(ACTION_TYPES.UTILS.POST_ANALYTICS, {
                ...eventDetails,
                screen: EVENT_ENUM?.[eventType]?.TITLE,
                funnel: "mypage",
                flow: "mypage",
                event_meta: eventId,
            })
        );
    };

    const resetDiscount = (): void => {
        dispatch(createAction(ACTION_TYPES.EVENT.RESET_DISCOUNT));
    };

    const handleClose =
        (isModal: boolean) =>
        (
            closeModalArg: IUseModal.IReturnType["closeModal"],
            modalInfoArg: IUtils.IModal
        ): void => {
            const { modalType: modalTypeArg, modalProps: modalPropsArg } =
                modalInfoArg;

            if (!isModal) {
                // Cases for when the event page is opened as a full page
                if (modalTypeArg === MODAL_TYPES.ExitIntent) {
                    const count: number =
                        getSessionStorageItem(STORAGE_KEYS.EXIT_INTENT_COUNT) ??
                        0;
                    setSessionStorageItem(
                        STORAGE_KEYS.EXIT_INTENT_COUNT,
                        count ? count + 1 : 1
                    );
                }
                closeModalArg();
            } else {
                // Cases for when the event page is opened as a modal

                // Close the modal if it is the base event modal
                if (modalTypeArg === MODAL_TYPES.EventModal) {
                    closeModalArg();
                    if (isDefined(hideSelf)) {
                        setModalInfo({
                            overrides: {},
                        });
                        dispatch(
                            createAction(
                                ACTION_TYPES.EVENT.SET_SELECTED_EVENT,
                                {
                                    selectedEvent: null,
                                }
                            )
                        );
                    }
                    return;
                }
                if (modalTypeArg === MODAL_TYPES.ExitOffer) {
                    setModalInfo({
                        modalType: MODAL_TYPES.EventModal,
                        modalProps: {
                            ...modalPropsArg.baseModalArgs,
                        },
                    });
                    return;
                }
                if (modalTypeArg === MODAL_TYPES.SoldOut) {
                    setModalInfo({
                        modalType: MODAL_TYPES.EventModal,
                        modalProps: {
                            ...modalPropsArg.baseModalArgs,
                            hasShownSoldOut: true,
                        },
                    });
                    return;
                }
                // Only close if the modal is closeable
                if (modalInfoArg.isCloseable) {
                    setModalInfo({
                        modalType: MODAL_TYPES.EventModal,
                        modalProps: { ...modalPropsArg.baseModalArgs },
                    });
                }
            }
        };

    const { handleOpen } = useOpenEventPageModal();

    useEffect(() => {
        // handling payment retry in case of merchandise containing variants
        // getting variant uuid in order data and setting selected variant in redux
        if (!transactionId || !isMerchandise || selectedVariant || !hasVariants)
            return;

        const orderObj = ordersMap?.[transactionId] || undefined;
        const orderData = orderObj?.orderData;
        const variantUuid = orderData?.variant_uuid;
        const isSuccess = orderData?.status === TRANSACTION_STATUSES.SUCCESS;

        if (!isSuccess)
            dispatch(
                createAction(ACTION_TYPES.EVENT.SET_SELECTED_VARIANT_BY_UUID, {
                    payload: {
                        eventId: eventData.uuid,
                        variantUuid,
                    },
                })
            );
    }, [transactionId, hasVariants, ordersMap]);

    useEffect(() => {
        setModalInfo({
            overrides: {
                handleClose: handleClose(isDefined(hideSelf)),
            },
        });
    }, []);

    const showExitIntent = () => {
        const showIntent = getSessionStorageItem(STORAGE_KEYS.BOOKED_DEMO);
        const count: number =
            getSessionStorageItem(STORAGE_KEYS.EXIT_INTENT_COUNT) ?? 0;
        if (
            !showIntent &&
            !isEmpty(hostData) &&
            !isEmpty(hostData?.exit_intent) &&
            count < 2
        ) {
            openModal(exitIntentModalToShow, {
                modalProps: {
                    baseModalArgs: modalInfo.modalProps,
                    ...(promoCoupons?.length > 0
                        ? {
                              coupons: promoCoupons,
                              applyDiscount: (code: string) =>
                                  callApplyDiscountApi(code, "normal"),
                          }
                        : {
                              onClickBack: closeModal,
                              hostName,
                              hostData,
                          }),
                },
            });
        }
    };

    useEffect(() => {
        if (hideSelf && !promoCoupons) return () => {};
        // No need for exit intent if isSiteBuilder === true

        // Using a callback that rebinds the dependencies since the callbacks
        // in the useEffect cannot read the updated values that aren't present
        // in the dependency array, but we cannot have rebinding of callbacks
        // without removing the previous version of bindings
        // https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback

        const showExitIntentPopup = () => {
            if (!modalInfo.isModalOpen) showExitIntent();
        };

        // show pop up when user mouse leaves the view after 30 seconds
        const onMouseLeave = () =>
            document.addEventListener("mouseleave", showExitIntentPopup);

        const timer1 = setTimeout(onMouseLeave, 30_000);
        // we cant track mouse leave in touch screens
        // so if mouseleave event does not happen till 45 sec show the popup
        const timer2 = setTimeout(showExitIntentPopup, 60_000);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            document.removeEventListener("mouseleave", showExitIntentPopup);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalInfo.isModalOpen]);

    /* 
        Resets Quantity and Diccount etc in 3 scenerios
        1. when user switches offerings (offering modal)
        2. when payment is succesfull
        3. or is in processing state
     */
    useEffect(() => {
        // Don't perform this reset if this is a payment failure case
        if (
            transactionId &&
            paymentStatus &&
            Number(paymentStatus) === TRANSACTION_STATUSES.FAILURE
        )
            return;

        resetDiscount();
        setQuantity(batched_flexible_sessions_count || 1);
        setNumberOfCustomers(1);
        setAppliedDiscountCodeDetails("not applied");
        setShowLoyaltyDiscountSection(true);
    }, [eventId]);

    // Log In as the first step of the payment flow
    const handleLogin = ({
        customerDetailsProp,
        hasSelectedGateway = false,
        isUserDetailsUpdateOtpVerificationSuccessful,
        successCallback,
        renewBookingCallback,
        paymentAvailabilitySuccessCallback,
        headCount,
    }: {
        customerDetailsProp: IUser.ICustomerDetailsProp;
        hasSelectedGateway?: boolean;
        isUserDetailsUpdateOtpVerificationSuccessful?: boolean;
        successCallback?: (k: IFetchDataAction.IReturnPayload) => void;
        renewBookingCallback?: (args: {
            response: IListing.IRebookingError;
            isRebooking?: boolean;
        }) => void;
        paymentAvailabilitySuccessCallback?: IAction.IPayloadProps["successCallback"];
        headCount?: number;
    }): void => {
        // Needs to be at least one customer
        if (customerDetailsProp?.length < 1) return;

        const { names, emails, phoneNumbers } = customerDetailsProp.reduce(
            (analyticsDataObj, customerDetails) => {
                const {
                    full_name: fullName,
                    email,
                    phone,
                    country_code: countryCode,
                } = customerDetails;

                analyticsDataObj.names.push(fullName as string);
                analyticsDataObj.emails.push(email as string);
                analyticsDataObj.phoneNumbers.push(`${countryCode}${phone}`);

                return analyticsDataObj;
            },
            {
                emails: [] as string[],
                names: [] as string[],
                phoneNumbers: [] as string[],
            }
        );

        postAnalytics(ANALYTICS_EVENTS.ADD_PAYMENT_INFO, {
            sub_domain: hostName,
            event_source_url: window.location.href,
            value: eventData.price_per_head,
            currency: eventData.currency_name,
            items: [
                {
                    item_id: eventData.uuid,
                    item_name: eventData.title,
                },
            ],
            user_data: {
                em: emails,
                ph: phoneNumbers,
                name: names,
            },
        });

        // Stripe needs a few extra address details, but in the initiate API.
        // So they are extracted here and passed in the metadata of the payment
        // API saga flow, to be picked up by the initiate payment saga
        const stripeCustomerDetails: IUser.ICustomerDetailsProp = [];

        // Add custom payment overrides here
        const customPaymentMethod: string = evaluatePaymentMethod({
            customerDetailsProp,
            hostData,
        });

        const customerDetails = customerDetailsProp.map(customerData => {
            const {
                customer_address: customerAddress,
                stripe_address: stripeAddress,
                city,
                postal_code: postalCode,
                payment_method: paymentMethod,
                ...returnData
            } = customerData;

            stripeCustomerDetails.push({
                // "stripe_address" needs to be sent to the API as "address"
                address: stripeAddress || customerAddress,
                city,
                state: returnData.billing_state,
                postal_code: postalCode,
            });
            return returnData;
        });

        // Check if the host is making the booking to their own listing
        // if (customerDetails[0].phone === hostData?.phone_number) {
        if (
            customerDetails.some(
                p =>
                    hostData?.phone_number &&
                    p?.phone === hostData?.phone_number
            )
        ) {
            // eslint-disable-next-line no-alert
            alert(
                (isMerchandise && "You cannot buy your own product") ||
                    (eventType === EVENT_TYPE.RECORDED_CONTENT &&
                        "You cannot buy your own content") ||
                    "You cannot book your own listing."
            );
            return;
        }

        clearPaymentCache();
        // Show the spinner
        handleStartProcessing("Booking...");

        // Store the data in localstorage for future referring

        setSessionStorageItem(
            STORAGE_KEYS.CUSTOMER_DETAILS,
            customerDetailsProp
        );
        setSessionStorageItem(
            STORAGE_KEYS.SELECTED_PAYMENT_GATEWAY,
            customPaymentMethod
        );

        const totalAmount: number = Number(
            getLocalStorageItem(STORAGE_KEYS.TOTAL_AMOUNT)
        );

        // Refer https://docs.google.com/spreadsheets/d/1eiUSbCNPaJ5RBrLOkL7eiRnd_u3racbi28QRFCzJkAs/edit?usp=sharing
        // for the logic below
        if (
            enablePaypalQueryParam &&
            // TODO: handle paypal in case of rebooking
            !isRenewBooking && // Fringe case, to be handled if need arises
            !hasSelectedGateway && // This function is being called for the first time
            !getCountryIsIndia(customerDetails[0].country as string) && // Phone number entered is international
            eventCurrency === CURRENCY_STRINGS.INR &&
            eventData?.paypal_link && // Paypal is enabled
            !areIntlPaymentsEnabled && // Razorpay is not available
            totalAmount > 0 // Total Amount is non zero
            // TODO: Single flag at the top of the module for the host's location
        ) {
            handleStopProcessing();
            handleOpen(MODAL_TYPES.PaymentSelection, {
                modalProps: {
                    // eslint-disable-next-line no-use-before-define
                    handleContinue: handlePaymentSelection,
                    handleClose: closeModal,
                    isCloseable: !isDirectBooking,
                },
            });
        } else {
            // Start the payment flow...
            const firstCustomerDetails = formatUserPaymentAuthPayload(
                customerDetails[0]
            );

            /**
             * We need this first cutomer to be saved on login for no slots fixed flow, as when a guest user has afterbooking questions to fill
             * His username and email won't be available also if we want to send whatsapp message we need these details for a guest user
             * */
            if (
                eventType === EVENT_TYPE.APPOINTMENTS &&
                eventData?.availability_choice ===
                    AVAILABILITY_CHOICE.NOT_DECIDED
            ) {
                setLocalStorageItem(
                    STORAGE_KEYS.CUSTOMER_NAME,
                    firstCustomerDetails?.full_name
                );
                setLocalStorageItem(
                    STORAGE_KEYS.EMAIL,
                    firstCustomerDetails?.email
                );
            }

            const customers = customerDetails.map(customerDetail =>
                formatUserPaymentAuthPayload(customerDetail)
            );

            dispatch(
                createAction(ACTION_TYPES.UTILS.API_CALL, {
                    apiActionType: isRenewBooking
                        ? API_ACTION_TYPES.PAYMENT_AUTH_RENEW
                        : API_ACTION_TYPES.PAYMENT_AUTH,
                    payload: isRenewBooking
                        ? {
                              ...firstCustomerDetails,
                              listing_uuid: eventId,
                              sub_domain: hostName,
                          } // in case of rebooking only one customer detail is sent
                        : {
                              customers,
                              sub_domain: hostName,
                          },
                    successAction: ACTION_TYPES.EVENT.CHECK_AVAILABILITY,
                    successCallback, // TODO: Move this success callback to metadata
                    paymentAvailabilitySuccessCallback,
                    metadata: {
                        ...(customerDetails.length > 0 &&
                            "customer_address" in customerDetails[0] && {
                                customerAddress:
                                    customerDetails?.[0]?.customer_address,
                            }),
                        // address packet will be same as stripe in case of merchandise
                        ...(isMerchandise && {
                            addressDetails: stripeCustomerDetails,
                        }),
                        // On whether to consider stripeCustomerDetails for payload processing.
                        hasToShowFullAddress,
                        // Check the beginning of the handleLogin function for explanation for this data
                        ...(hasToShowFullAddress && {
                            stripeCustomerDetails,
                            customPaymentMethod,
                        }),
                        isRebooking: isRenewBooking,
                        paymentAvailabilityErrorCallback: (
                            response: IListing.IRebookingError
                        ) =>
                            renewBookingCallback?.({
                                response,
                                isRebooking: isRenewBooking,
                            }),
                        selectedPaymentType,
                        ...(headCount && {
                            headCount,
                        }),
                        initialInstalment: initialPayment,
                        isUserDetailsUpdateOtpVerificationSuccessful,
                        customers,
                        isCustomerDetailsUpdationAllowed,
                    },
                    errorCallback: () => {
                        handleStopProcessing();
                    },
                    // TODO: Move this finally callback to metadata
                    // TODO: proper type declaration for function args
                    finallyCallback: ({
                        apiCallArgs: { apiActionType },
                    }: any) => {
                        // to stop processing in case payment gateway is closed
                        if (
                            apiActionType === API_ACTION_TYPES.PAYMENT_INITIATE
                        ) {
                            handleStopProcessing();
                        }
                    },
                })
            );
        }
    };

    let formDetailsProp: { [key: string]: Array<IUser.IFormDataVal> } = {};
    if (appliedDiscountCodeType === "loyalty") {
        formDetailsProp = {
            [getUserDetailsFormKey(1)]: [
                {
                    key: USER_DETAILS_FORM_FIELDS.email,
                    value: loyaltyDiscountEmail,
                    editingDisabled: true,
                },
            ],
        };
    }

    // prefilling pincode in case of shiprocket enabled
    if (isMerchandise && shiprocketEnabled) {
        const prevData = formDetailsProp[getUserDetailsFormKey(1)] || [];
        formDetailsProp = {
            ...formDetailsProp,
            [getUserDetailsFormKey(1)]: [
                ...prevData,
                {
                    key: USER_DETAILS_FORM_EXTRA_FIELD.postal_code,
                    value: shippingDetails?.pincode,
                    editingDisabled: true,
                },
            ],
        };
    }

    const isValidMerchandiseOffering = isMerchandise
        ? areSlotsAvailable &&
          !eventData?.metadata?.merchandise?.no_address_needed
        : Object.values(selectedUpsellOfferingsQuantities).some(
              o => "quantity" in o
          );

    const MODAL_PROPS = {
        [MODAL_TYPES.UserDetails]: ({
            isBooking: isBookingProp,
            ...rest
        }: {
            isBooking: boolean;
            [k: string]: any;
        }) => ({
            isBooking: isBookingProp ?? false,
            isEventPage: true,
            isCloseable: !isDirectBooking || enableWebinarDirectBooking,
            isValidMerchandise: isBookingProp && isValidMerchandiseOffering,
            ...(isBookingProp && { handleLogin, hasToShowFullAddress }),
            formDetailsProp,
            showGstOption,
            billingStateNonMandatory,
            ...rest,
        }),
    };

    const handlePaymentSelection = (
        countryType: string,
        successCallback?: () => void
    ): void => {
        try {
            const customerDetails: { [k: string]: string }[] =
                getSessionStorageItem(STORAGE_KEYS.CUSTOMER_DETAILS) || [];
            if (customerDetails && !isEmpty(customerDetails)) {
                updateBookingData({
                    gatewaysSupportedProp:
                        countryType !== CURRENCY_STRINGS.INR &&
                        enablePaypalQueryParam
                            ? [GATEWAYS_ENUM.PAYPAL]
                            : GATEWAYS_SUPPORTED,
                });
                handleLogin({
                    customerDetailsProp: customerDetails,
                    hasSelectedGateway: true,
                    successCallback: () => {
                        successCallback?.();

                        if (countryType !== CURRENCY_STRINGS.INR) {
                            handleOpen(MODAL_TYPES.PaymentNotification, {
                                modalProps: {
                                    handleClose: closeModal,
                                    eventData,
                                    totalPrice,
                                    isCloseable: !isDirectBooking,
                                },
                            });
                        }
                    },
                });
            }
        } catch (error) {
            // TODO: Add extensive error handling
            alert("An error has occurred. Please try again.");
            // eslint-disable-next-line no-console
            console.log("Error");
        }
    };

    const handleStartProcessing = (customProcessingMessage?: string) => {
        startProcessing();
        setProcessingMessage(customProcessingMessage);
    };

    const handleStopProcessing = () => {
        stopProcessing();
        setProcessingMessage(undefined);
    };

    /**
     * handleStartBookingFlow changelog:
     * Date: 8th Dec 2023:
     * -> Feature: Other SKUs in Live Sessions
     *    Changes:
     *    1. When user lands on event page on clicking on "Book now" cta in live sessions
     *       in Recorded Content Course or bonus offering in Branded Community,
     *       then on clicking on purchase offering cta, in user details form:
     *       - If the only requirements to attend to, are: Name, email, phone number - then
     *         clicking on ‘Book now’ will:
     *          -> Take them to the payment gateway directly (if the offering is paid).
     *             /OR/ Show payment confirmation popup (if the offering is free).
     *          -> The ‘Initiate booking’ popup will not appear in the flow. As for the
     *             data that we need for this booking (i.e. name, email and phone number),
     *             it will be the same as the details previousely used while booking the course/community.
     *          -> The flow from the payment gateway onwards will be the same as usual.
     *    2. If the requirements to attend to, is anything more than the three fields: Name, email, phone number
     *       (for eg: gst details checkbox, address field in merchandise, pre-booking form questions, etc),
     *       then clicking on ‘Book now’ will:
     *          -> Open the ‘Initiate booking’ popup but with the old details (from the course/community purchase) pre-filled.
     */
    const handleStartBookingFlow = (
        extraModalProps?: IUseModal.IOpenModalProps["modalProps"]
    ): void => {
        handleEventStarted({ eventData, selectedSlots: slots });

        postAnalyticalEvent({
            event_name: "bookNow",
            action: "Tap",
            ui_component: "Button",
            identifier: "Book Now",
        });

        if (isBrowser() && !startBookingFlowAnalyticsTriggered) {
            setStartBookingFlowAnalyticsTriggered(true);
        }

        const canSkipUserDetailsForm = getCanSkipUserDetailsForm({
            eventData,
            hostData,
            numberOfCustomers,
            isValidMerchandiseOffering,
            hasToShowFullAddress,
            showGstOption,
        });

        if (canSkipUserDetailsForm) {
            const currentCustomerDetailsPaymentPayload =
                getCustomerDetailsForPaymentPayload();
            const allDetailsExistsForCurrentCustomer = !Object.values(
                currentCustomerDetailsPaymentPayload
            ).some(customerDetail => !customerDetail);

            if (allDetailsExistsForCurrentCustomer) {
                removeOverridesFromUDMODB();
                handleLogin({
                    customerDetailsProp: [currentCustomerDetailsPaymentPayload],
                    renewBookingCallback: handleRebookingError,
                });
                return;
            }
        }

        if (isRenewBooking) {
            removeOverridesFromUDMODB();
            handleRenewBookingCtaClick(handleLogin);
        } else {
            handleOpen(MODAL_TYPES.UserDetails, {
                modalProps: MODAL_PROPS[MODAL_TYPES.UserDetails]({
                    isBooking: true,
                    ...extraModalProps,
                }),
            });
        }
    };

    const showGuestInfoModal = (): void => {
        // For the cases when user info is needed but not to initiate payment flow
        handleOpen(MODAL_TYPES.UserDetails, {
            modalProps: {
                isBooking: false,
                isEventPage: true,
                formDetailsProp,
            },
        });
    };

    // TODO @mayank: try commenting this out and see if navigation between
    // event and host page works
    useEffect(() => {
        // If hideSelf is defined then this beforePopState callback already exists
        if (!hideSelf) {
            // Hide take the page back when browser nav happens
            router.beforePopState(({ url, as: asProp }) => {
                if (url.startsWith("/host") && asProp.startsWith("/")) {
                    router.push("/");
                }
                return true;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router]);

    // For events of type Appointments and Classes, slots can be changed/selected so this is to record analytics for that
    useEffect(() => {
        if (
            [
                EVENT_TYPE.APPOINTMENTS,
                EVENT_TYPE.ONE_ON_ONE_CLASSES,
                EVENT_TYPE.GROUP_CLASSES,
                EVENT_TYPE.ROLLING_CLASSES,
                EVENT_TYPE.WEBINAR,
                // * NOTE: Should Merchandise quantity be also tracked?
            ].includes(eventType)
        ) {
            postAnalyticalEvent({
                event_name: "slotSelected",
                action: "Tap",
                ui_component: "Button",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slots]);

    const showCompactEventPage = getShowCompactEventPage({ urlQueryParams });

    const isMobileDeviceWidth: boolean = useCustomMediaQuery(600);

    // in case CompactEventPage is shown, the layout is similar to sidebar in desktop view,
    // so we keep isMobile = false
    const isMobile = evaluateRenderMobileView({
        showCompactEventPage,
        isMobileDeviceWidth,
    });

    // Props for ScheduleSelector component
    const scheduleSelectorProps: IScheduleSelectorProps = {
        showGuestInfoModal,
        isMobile,
        componentRef: scheduleSelectorRef,
        disablePayment,
    };

    const expressInterestProps: IExpressInterestProps = {
        hostName,
        disablePayment,
        isSoldOut,
        isMobileDeviceWidth,
        urlQueryParams,
    };

    const isOpenedAsEventModal = !!hideSelf;

    // Props for the PaymentSummary component
    const paymentSummaryProps: IPaymentSummary.IProps = {
        disablePayment,
        isOpenedAsEventModal,
        isMobile,
        urlQueryParams,
    };

    useSetPaymentSummaryProps({ paymentSummaryProps });

    const planDropdownProps: IPlanDropdown.IProps = {
        isMobile,
    };

    const cartData = getCartData();

    const hideAddToCartCta = evaluateHideAddToCartCta({
        hostData,
        disablePayment,
        dynamicLink,
        isRebooking: isRenewBooking,
    });

    const isInCartFlow = getIsInCartFlow({
        userFlow,
        hostData,
        disablePayment,
        dynamicLink,
        isRebooking: isRenewBooking,
    });

    const handleMainCta = (props?: IUseModal.IOpenModalProps["modalProps"]) => {
        if (isMainCtaDisabled) {
            handleDisabledCtaClick({
                disablePayment,
                event,
                eventData,
                scheduleSelectorRef,
                needUserInputToProceed,
                messageForUserToProceed,
            });
        } else if (!disablePayment) {
            handleStartBookingFlow(props);
        }
    };

    const addToCartCtaProps: IAddToCartCtaProps = {
        scheduleSelectorRef,
        addToCartCallback,
        closeModal,
        isCtaDisabled: isMainCtaDisabled,
        dynamicLink,
        isRebooking: isRenewBooking,
        userFlow,
        hostName,
    };

    const mainCtaProps: IMainCtaProps = {
        hideComponent: isInCartFlow,
        onClick: handleMainCta,
        ctaText: getListingMainCtaText({
            eventType,
            customText: eventData?.metadata?.booking_cta_text,
            isProcessing,
            processingMessage,
        }),
    };

    const ctaButtonProps: ICTAButtons.IProps = {
        mainCtaProps,
        addToCartCtaProps,
        hostName,
    };

    const callApplyDiscountApi = (
        newDiscountCode: string,
        newDiscountCodeType: IEvent.IAppliedDiscountCodeType,
        newCustomerEmail: string = ""
    ) => {
        const formattedNewDiscountCode = (newDiscountCode || "")
            .trim()
            .toUpperCase();
        const appliedCount = Object.values(cartData?.listingMap || {}).filter(
            listing => {
                const cartItemDiscountCode = (
                    listing?.discount?.discountCode || ""
                )
                    .trim()
                    .toUpperCase();

                return (
                    listing?.eventData?.uuid !== eventData?.uuid &&
                    cartItemDiscountCode &&
                    formattedNewDiscountCode &&
                    cartItemDiscountCode === formattedNewDiscountCode
                );
            }
        ).length;

        if (isInstallmentsPlanType(payment_plan_type)) {
            handleApplyDiscountOnInstallments({
                discountCode: formattedNewDiscountCode,
                currency: eventCurrency,
                ...(!hideAddToCartCta && { appliedCount }),
            });
        }
        const selectedOfferingVariation = eventData?.selectedOfferingVariation;

        dispatch(
            createAction(ACTION_TYPES.UTILS.API_CALL, {
                apiActionType: API_ACTION_TYPES.APPLY_DISCOUNT,
                payload: {
                    code: formattedNewDiscountCode,
                    ...(!hideAddToCartCta && {
                        applied_count: appliedCount,
                    }),
                    listing_uuid: eventData?.uuid,
                    ...(selectedOfferingVariation && {
                        offering_variant_uuid: selectedOfferingVariation.uuid,
                    }),
                    currency: eventCurrency,
                    is_upsell_order:
                        showUpsellListings &&
                        Object.keys(selectedUpsellOfferingsQuantities).length >
                            0,
                    customer_email:
                        newDiscountCodeType === "loyalty" && newCustomerEmail,
                    discountCodeType: newDiscountCodeType,
                    ...(!checkIsStringEmpty(dynamicLink) && {
                        dynamic_link: dynamicLink,
                    }),
                },
                errorCallback: error => {
                    logError({ error });
                    alert(error?.response?.message ?? "Something went wrong!");
                },
            })
        );
    };

    useEffect(() => {
        // in case of modal loaded from cart flow, we need to update the discount, quantity and slots from session storage

        if (isInCartFlow) {
            const cartEventData = cartData?.listingMap?.[eventId];

            const cartDiscount = cartEventData?.discount;
            const cartDiscountCode = cartDiscount?.discountCode;
            const discountCodeType = cartDiscount?.appliedDiscountCodeType;
            const customerEmail = cartDiscount?.loyaltyDiscountEmail;
            if (cartDiscountCode) {
                callApplyDiscountApi(
                    cartDiscountCode,
                    discountCodeType,
                    customerEmail
                );
                setAppliedDiscountCodeDetails(discountCodeType, customerEmail);
            }

            // we are setting slots in src/components/eventPage/components/schedule/SlotDropdown/index.tsx

            const cartQuantity = cartEventData?.quantity;
            if (cartQuantity) setQuantity(cartQuantity);
        }
    }, []);

    const ticketCountProps: ITicketCount.IProps = {
        userFlow,
        disablePayment,
        communityUuid,
        dynamicLink,
        isRebooking: isRenewBooking,
    };

    const reviewsProps: IReviewsProps = {
        closeModal,
    };

    // On the presence of the `init_booking` GET param, open the booking modal if possible
    useEffect(() => {
        if (!hideSelf) {
            updateBookingData({ isDirectBookingProp: isDirectBooking });
            // Check that the page is not opened for post-payment status
            if (!(transactionId && paymentStatus)) {
                if (isDirectBooking) {
                    ctaButtonProps.mainCtaProps.onClick?.({
                        directBookingDiscount: !!(
                            (directBookingDiscount && !hideDiscount) // Won't work if hide discount is enabled
                        ),
                    });
                    if (!enableWebinarDirectBooking)
                        setModalInfo({
                            isCloseable: false,
                        });
                } else if (initContact) {
                    removeOverridesFromUDMODB();
                    handleOpen(MODAL_TYPES.UserDetails, {
                        modalProps: MODAL_PROPS[MODAL_TYPES.UserDetails]({
                            isBooking: false,
                        }),
                    });
                } else if (isProcessing) {
                    // do nothing
                    // dont remove overrides from UDMODB in this case
                } else {
                    removeOverridesFromUDMODB();
                }
            } else {
                removeOverridesFromUDMODB();
            }
        } else {
            removeOverridesFromUDMODB();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDirectBooking, initContact, hasToShowFullAddress]);
    // `hasToShowFullAddress` has to be included in the dependency array here since if the user is determined to be an
    // international user and the event data re-fetch allows for an international booking, international, their address
    // needs to be collected for stripe, but without having the responsible parameter `hasToShowFullAddress` in the
    // dependency array here, the user details modal will open with the old value of `false`.
    /** END of hack */

    // Calculate the bottom spacing of the last section depending on the kind of bottom overlays shown
    const getBottomSpacing = () => {
        return !isMobile
            ? ""
            : `${[
                  // [1]: Base
                  100, // Base spacing

                  // [2]: Payment Summary Overlay - Closed state
                  hidePrice || // For the all-covering express interest case set by the back-end
                  !areSlotsAvailable || // For the express-interest case shown when no slots are available
                  (areSlotsAvailable && !(totalPrice === 0)) // For the case where price of the selected items is non-zero
                      ? 70
                      : 0,

                  // [3]: Early Bird
                  earlyBirdObj && areSlotsAvailable && !(totalPrice === 0) // For the early bird strip
                      ? 20
                      : 0,
              ].reduce((sum, curr) => sum + curr, 0)}px`;
    };

    const bottomSpacing = getBottomSpacing();

    useEffect(() => {
        setCssVar(
            getCssKeyName("body-bottom-spacing", "event-page"),
            bottomSpacing
        );
    }, [bottomSpacing]);

    // TODO: Turn all <show/hide>OnlyOnMobile to jsx render conditions instead of just css

    // props migrated from child components
    const eventModuleChildProps: IEventModule.ChildProps =
        getEventModuleChildProps({
            dispatch,
            event,
            eventData,
            host,
            eventId,
            planId,
            isMobile,
            dynamicLink,
            showGuestInfoModal,
            handleOpenModal: handleOpen,
            mainScrolledComponentRef,
            hideDiscount,
            callApplyDiscountApi,
            disablePayment,
            showLoyaltyDiscountsPopup,
            setShowLoyaltyDiscountsPopup,
            showLoyaltyDiscountSection,
            computedPaymentValues,
            wrapperComponentType,
            getDropdownSelectorsProps,
            isDiscountAppliedOnInstallments,
            refetchInstallments,
        });

    const recentSalesPopupProps: IRecentSalesPopup.IProps = {
        enabled: hostData?.recent_sales_popup && eventData?.recent_sales_popup,
        coverImage: eventData?.cover_image,
        eventType,
        eventTitle: eventData?.title,
    };

    const upsellListingsProps: IUpsellListings.IProps = {
        disablePayment,
        urlQueryParams,
        resetDiscount,
        setAppliedDiscountCodeDetails,
        setShowLoyaltyDiscountSection,
    };

    // TODO: rename it to orderBump instead of upsell
    useSetUpsellListingProps({ upsellListingsProps });

    const earlyBirdProps: IEarlyBird.IProps = {
        disablePayment,
        isMobile,
        urlQueryParams,
    };

    const { hideTillOfferingNotSelectedInPlan } =
        useReadIsOfferingSelectedInPlan({ urlQueryParams });

    const hideTillVariantNotSelected =
        (!!variantsData?.variants && !selectedVariant) ||
        (eventData.is_offering_variants_enabled === true &&
            !eventData?.selectedOfferingVariation);

    const compactEventPageProps: ICompactEventPageModule.IProps = {
        eventId,
        hideSelf,
        showOpaqueBackdrop,
        eventData,
        eventType,
        planDropdownProps,
        hideTillOfferingNotSelectedInPlan,
        hideTillVariantNotSelected,
        disablePayment,
        isSoldOut,
        scheduleSelectorProps,
        recentSalesPopupProps,
        ctaButtonProps,
        ticketCountProps,
        paymentSummaryProps,
        expressInterestProps,
        earlyBirdProps,
        slots,
        hostName,
        // props migrated from child components
        ...eventModuleChildProps,
    };
    const completeEventPageProps: ICompleteEventPageModule.IProps = {
        mainScrolledComponentRef,
        urlQueryParams,
        isMobile,
        hostData,
        eventCurrencySymbol,
        hidePrice,
        price,
        isSingleSlot,
        billingMultiplier,
        pricePerHead,
        earlyBirdObj,
        reviewsProps,
        isEventModal,
        ...compactEventPageProps,
    };

    return (
        <>
            {showCompactEventPage ? (
                <CompactEventPage {...compactEventPageProps} />
            ) : (
                <CompleteEventPage {...completeEventPageProps} />
            )}
            <RebookingConfirmationModal
                {...rebookingModalState}
                onClose={closeRebookingModal}
            />
        </>
    );
};

// The goal should be to keep this page length < 1000 lines
